<script>
export default {
  name: "PickingListPanel",
  props: {
    scanBlocked: { type: Boolean, default: false },
    selectedOrder: {
      type: Object,
      default: null
    },
    progressText: {
      type: String,
      default: ""
    },
    itemBarcodeInput: {
      type: String,
      default: ""
    },
    hblCompleteInput: {
      type: String,
      default: ""
    },
    finalWeightInput: {
      type: [String, Number],
      default: ""
    },
    canScanItems: {
      type: Boolean,
      default: false
    },
    canCompletePacking: {
      type: Boolean,
      default: false
    },
    isSubmittingComplete: {
      type: Boolean,
      default: false
    },
    completeDisabledReason: {
      type: String,
      default: ""
    },
    isFinalHblConfirmed: {
      type: Boolean,
      default: false
    },
    canSubmitPackingCompletion: {
      type: Boolean,
      default: false
    },
    isAllItemsCompleted: {
      type: Boolean,
      default: false
    },
    pendingItems: {
      type: Array,
      default: () => []
    },
    packingItems: {
      type: Array,
      default: () => []
    }
  },
  emits: [
    "scan-key",
    "update:itemBarcodeInput",
    "update:hblCompleteInput",
    "update:finalWeightInput",
    "update:isComposing",
    "scan-item",
    "complete-packing",
    "submit-completion",
    "adjust-qty"
  ],
  methods: {
    onItemInput(value) {
      this.$emit("update:itemBarcodeInput", value)
    },
    onCompleteInput(value) {
      this.$emit("update:hblCompleteInput", value)
    },
    onFinalWeightInput(value) {
      this.$emit("update:finalWeightInput", value)
    },
    onScanItem() {
      this.$emit("scan-item")
    },
    onCompletePacking() {
      this.$emit("complete-packing")
    },
    onSubmitCompletion() {
      this.$emit("submit-completion")
    },
    onAdjustQty(item, delta) {
      this.$emit("adjust-qty", item, delta)
    },
    formatWeight(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n <= 0) return "-"
      return `${Math.round(n * 1000) / 1000}kg`
    },
    weightSourceLabel(item) {
      return item?.weightSource === "FULFILLMENT" ? "풀필먼트" : "주문입력"
    }
  }
}
</script>

<template>
  <div class="comp-box" v-if="selectedOrder">
    <div class="row items-center q-mb-sm">
      <div class="col">
        <h6 class="section-title q-mt-none q-mb-none">3) 상품 피킹 리스트</h6>
      </div>
      <div class="col-auto">
        <q-badge color="primary" outline>{{ progressText }}</q-badge>
      </div>
    </div>

    <div class="row q-col-gutter-lg">
      <div class="col-12 col-md-6">
        <q-input
          ref="itemScanInputRef"
          :readonly="scanBlocked"
          @keydown.capture="$emit('scan-key', $event)"
          @keyup.capture="$emit('scan-key', $event)"
          :model-value="itemBarcodeInput"
          label="상품 바코드 스캔"
          :disable="!canScanItems"
          @update:model-value="onItemInput"
          @keyup.enter="onScanItem"
          @compositionstart="$emit('update:isComposing', true)"
          @compositionend="$emit('update:isComposing', false)"
        >
          <template v-slot:append>
            <q-btn
              color="primary"
              label="스캔 반영"
              :disable="!canScanItems || scanBlocked"
              @click="onScanItem"
            />
          </template>
        </q-input>
      </div>
      <div class="col-12 col-md-6">
        <q-input
          ref="hblCompleteInputRef"
          :model-value="hblCompleteInput"
          label="HBL 재스캔(최종 확인용)"
          :disable="!canCompletePacking || isFinalHblConfirmed"
          @update:model-value="onCompleteInput"
          @keyup.enter="onCompletePacking"
          @compositionstart="$emit('update:isComposing', true)"
          @compositionend="$emit('update:isComposing', false)"
        >
          <template v-slot:append>
            <q-btn
              color="positive"
              label="HBL 확인"
              :disable="!canCompletePacking || isSubmittingComplete"
              :loading="isSubmittingComplete"
              @click="onCompletePacking"
            />
          </template>
        </q-input>
      </div>
      <div v-if="isFinalHblConfirmed" class="col-12">
        <q-input
          ref="finalWeightInputRef"
          :model-value="finalWeightInput"
          type="number"
          min="0.01"
          max="9999.99"
          step="0.01"
          label="최종 박스 무게"
          suffix="kg"
          autofocus
          @update:model-value="onFinalWeightInput"
          @keyup.enter="onSubmitCompletion"
        >
          <template v-slot:append>
            <q-btn
              color="positive"
              label="포장 완료"
              :disable="!canSubmitPackingCompletion"
              :loading="isSubmittingComplete"
              @click="onSubmitCompletion"
            />
          </template>
        </q-input>
      </div>
    </div>

    <q-markup-table flat bordered class="q-mt-md">
      <thead>
        <tr>
          <th class="text-left">상품명</th>
          <th class="text-left">바코드</th>
          <th class="text-right">적용무게</th>
          <th class="text-right">필요수량</th>
          <th class="text-right">담은수량</th>
          <th class="text-center">상태</th>
          <th class="text-center">수동수정</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in packingItems" :key="item.id">
          <td>{{ item.name }}</td>
          <td>{{ item.barcode }}</td>
          <td class="text-right">
            <div>{{ formatWeight(item.effectiveWeight) }}</div>
            <q-badge
              :color="item.weightSource === 'FULFILLMENT' ? 'indigo-7' : 'grey-6'"
              text-color="white"
              class="weight-source-badge">
              {{ weightSourceLabel(item) }}
            </q-badge>
          </td>
          <td class="text-right">{{ item.requiredQty }}</td>
          <td class="text-right">{{ item.pickedQty }}</td>
          <td class="text-center">
            <q-badge
              :color="item.status === '완료' ? 'positive' : (item.status === '진행중' ? 'warning' : 'grey-6')"
              :text-color="item.status === '진행중' ? 'dark' : 'white'"
            >
              {{ item.status }}
            </q-badge>
          </td>
          <td class="text-center">
            <q-btn dense flat icon="remove" :disable="!canScanItems || scanBlocked" @click="onAdjustQty(item, -1)" />
            <q-btn dense flat icon="add" :disable="!canScanItems || scanBlocked" @click="onAdjustQty(item, 1)" />
          </td>
        </tr>
      </tbody>
    </q-markup-table>

    <div
      v-if="(!canCompletePacking || isFinalHblConfirmed) && completeDisabledReason"
      class="text-caption text-grey-7 q-mt-xs"
    >
      {{ completeDisabledReason }}
    </div>
    <div
      v-if="selectedOrder && !isAllItemsCompleted && pendingItems.length"
      class="pending-list q-mt-sm"
    >
      <div class="pending-title">부족 품목</div>
      <div
        v-for="item in pendingItems"
        :key="`pending-${item.id}`"
        class="pending-row"
      >
        {{ item.name }}: {{ item.remainingQty }}개 부족 ({{ item.pickedQty }}/{{ item.requiredQty }})
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.section-title {
  font-size: 1rem;
  font-weight: 700;
}

.pending-list {
  border: 1px solid #f1c0c0;
  background: #fff7f7;
  border-radius: 6px;
  padding: 8px;
}

.pending-title {
  font-size: 12px;
  font-weight: 700;
  color: #b42318;
  margin-bottom: 4px;
}

.pending-row {
  font-size: 12px;
  color: #5f2120;
}

.weight-source-badge {
  margin-top: 3px;
  font-size: 11px;
}
</style>
